import styles from "../../styles";

/**
 * Component for rendering restart confirmation popup
 */
export class RestartConfirmationComponent extends HTMLElement {
    private _onConfirm: (() => void) | null = null;
    private _onCancel: (() => void) | null = null;

    /**
     * Set the confirm handler
     */
    public set onConfirm(handler: () => void) {
        this._onConfirm = handler;
    }

    /**
     * Set the cancel handler
     */
    public set onCancel(handler: () => void) {
        this._onCancel = handler;
    }

    /**
     * Called when the element is connected to the DOM
     */
    public connectedCallback(): void {
        this.render();
        this.setupEventListeners();
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    public disconnectedCallback(): void {
        this.removeEventListeners();
    }

    /**
     * Render the component
     */
    private render(): void {
        const content: string = `
            <style>${styles.popups}</style>
            <div class="popup-overlay">
                <div class="popup">
                    <div class="restart-confirmation">
                        <h3>Are you sure?</h3>
                        <p>This will restart the game and delete all your progress.</p>
                        <div class="restart-buttons">
                            <button id="restart-confirm-button" class="restart-confirm-button">Yes, Restart Game</button>
                            <button id="restart-cancel-button" class="restart-cancel-button">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        this.innerHTML = content;
    }

    /**
     * Setup event listeners
     */
    private setupEventListeners(): void {
        // Confirm button click
        const confirmButton: HTMLElement | null = this.querySelector("#restart-confirm-button");
        if (confirmButton) {
            confirmButton.addEventListener("click", this.handleConfirm.bind(this));
        }

        // Cancel button click
        const cancelButton: HTMLElement | null = this.querySelector("#restart-cancel-button");
        if (cancelButton) {
            cancelButton.addEventListener("click", this.handleCancel.bind(this));
        }

        // Outside click
        const overlay: HTMLElement | null = this.querySelector(".popup-overlay");
        if (overlay) {
            overlay.addEventListener("click", this.handleOutsideClick.bind(this));
        }

        // Prevent clicks inside popup from closing it
        const popup: HTMLElement | null = this.querySelector(".popup");
        if (popup) {
            popup.addEventListener("click", (e: Event) => e.stopPropagation());
        }
    }

    /**
     * Remove event listeners
     */
    private removeEventListeners(): void {
        const confirmButton: HTMLElement | null = this.querySelector("#restart-confirm-button");
        if (confirmButton) {
            confirmButton.removeEventListener("click", this.handleConfirm.bind(this));
        }

        const cancelButton: HTMLElement | null = this.querySelector("#restart-cancel-button");
        if (cancelButton) {
            cancelButton.removeEventListener("click", this.handleCancel.bind(this));
        }

        const overlay: HTMLElement | null = this.querySelector(".popup-overlay");
        if (overlay) {
            overlay.removeEventListener("click", this.handleOutsideClick.bind(this));
        }
    }

    /**
     * Handle confirm button click
     */
    private handleConfirm(): void {
        if (this._onConfirm) {
            this._onConfirm();
        }
        this.remove();
    }

    /**
     * Handle cancel button click
     */
    private handleCancel(): void {
        if (this._onCancel) {
            this._onCancel();
        }
        this.remove();
    }

    /**
     * Handle click outside the popup
     */
    private handleOutsideClick(event: Event): void {
        const target: HTMLElement | null = event.target as HTMLElement;
        if (target.classList.contains("popup-overlay")) {
            this.handleCancel();
        }
    }
}
